import { isPublicLink } from "@/helpers/services/images";
import { i18n } from "@/plugins/vue-i18n";
import { debounce } from "vuetify/lib/util/helpers";

// Utils

/**
 * Caches from getImageSizeFromUrl() function results.
 * @type {Object.<string, {width: number, height: number}>}
 */
const imagesSizes = {};

/**
 * Retrieves the size (width and height) of an image from its URL.
 * Caches the results to avoid redundant image loading.
 * @param {string} url - The URL of the image.
 * @returns {Promise<{width: number, height: number} | {}}>} - A promise that resolves to an object containing the width and height, or an empty object if an error occurs.
 */
function getImageSizeFromUrl(url) {
  if (imagesSizes[url]) return imagesSizes[url];
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = function () {
      imagesSizes[url] = { width: img.width, height: img.height };
      resolve(imagesSizes[url]);
    };
    img.onerror = function () {
      resolve({});
    };
    img.src = url;
  });
}

/**
 * Extracts numbers from a string, removing all non-numeric characters.
 * @param {string} str - The input string.
 * @returns {string} - A string containing only the numeric characters from the input.
 */
function extractNumbers(str) {
  if (!str) {
    return "";
  }
  return str.replace(/[^0-9]/g, "");
}

/**
 * Converts a camelCase string to kebab-case (e.g., myVariableName to my-variable-name).
 * @param {string} str - The camelCase string to convert.
 * @returns {string} - The kebab-case equivalent of the input string.
 */
function camelToKebab(str) {
  return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, "$1-$2").toLowerCase();
}
//

/**
 * @type {string}
 * @description Label for the image properties menu option.
 */
const menuOptionLabel = i18n.t("htmlEditor.imageMenu.option");

/**
 * @type {string}
 * @description Label for the insert/edit image dialog.
 */
const menuDialogLabel = i18n.t("htmlEditor.imageMenu.dialog");

/**
 * @type {boolean}
 * @description Flag to indicate if there's a URL error.
 */
let urlError = false;

/**
 * Builds the CSS styles for displaying an error message, particularly for URL validation errors.
 * @param {object} { urlError } - An object containing a boolean indicating if there's a URL error.
 * @returns {string} - A CSS style string for the error message element.
 */
function buildErrorMessageStyles({ urlError }) {
  return [
    "margin-top: 4px;",
    "line-height: 12px;",
    "word-break: break-word;",
    "overflow-wrap: break-word;",
    "word-wrap: break-word;",
    "-ms-hyphens: auto;",
    "hyphens: auto;",
    "color: var(--v-error-base) !important;",
    "caret-color: var(--v-error-base) !important;",
    "text-align: left;",
    "font-size: 12px;",
    "letter-spacing: .0178571429em !important;",
    "font-weight: 400;",
    "font-family: Roboto, sans-serif !important;",
    urlError ? "display: block;" : "display: none;",
  ].join(" ");
}

/**
 * Builds the CSS styles for the URL input label, changing its color to indicate a URL error.
 * @param {object} { urlError } - An object containing a boolean indicating if there's a URL error.
 * @returns {string} - A CSS style string for the URL input label.
 */
function buildUrlInputLabelStyles({ urlError }) {
  return [
    urlError
      ? "color: var(--v-error-base) !important;"
      : "color: rgba(34, 47, 62, .7);",
    "font-size: 14px;",
    "font-style: normal;",
    "font-weight: 400;",
    "line-height: 1.3;",
    "padding: 0 8px 0 0;",
    "text-transform: none;",
    "white-space: nowrap;",
    "margin-bottom: -4px;",
    "display: flex;",
  ].join(" ");
}

// Menu builders

/**
 * Formats the initial data for the image menu, ensuring all necessary fields are present
 * and have default values if not provided.
 * @param {object} data - The input data, potentially incomplete.
 * @returns {object} - The formatted data with all required fields.
 */
function formatInitialData(data = {}) {
  return {
    src: data.src || "",
    alt: data.alt || "",
    size: {
      width: data.size?.width || "",
      height: data.size?.height || "",
    },
    style: data.style || "",
    borderClass: data.borderClass || "",
    verticalMargin: data.verticalMargin || "",
    horizontalMargin: data.horizontalMargin || "",
    borderWidth: data.borderWidth || "",
    borderStyle: data.borderStyle || "",
  };
}

/**
 * Builds the initial data for the image menu based on an existing image node in the editor.
 * Extracts relevant attributes (src, alt, size, style, etc.) from the node.
 * @param {HTMLElement} node - The HTML image element node.
 * @returns {object} - The initial data object for the menu, populated from the node's attributes.
 */
function buildInitialDataFromNode(node) {
  return formatInitialData({
    src: node.src,
    alt: node.alt,
    size: {
      width: node.width?.toString(),
      height: node.height?.toString(),
    },
    style: Object.entries(node.style).reduce((acc, [key, value]) => {
      if (!value || !isNaN(parseInt(key))) return acc;
      const kebabKey = camelToKebab(key);
      acc += `${kebabKey}: ${value};`;
      return acc;
    }, ""),
    borderClass: node.className,
    verticalMargin: extractNumbers(node.style.marginTop),
    horizontalMargin: extractNumbers(node.style.marginLeft),
    borderWidth: extractNumbers(node.style.borderWidth),
    borderStyle: node.style.borderStyle,
  });
}

/**
 * Builds the default initial data for the image menu when no image node is selected.
 * Provides empty or default values for all fields.
 * @returns {object} - The default initial data object for the menu.
 */
function buildInitialDataDefault() {
  return formatInitialData();
}

/**
 * Builds the "General" tab configuration for the image menu.
 * Includes input fields for image source, alternative text, dimensions, and border style.
 * @param {object} { urlError = false } - An object containing a boolean indicating if there's a URL error.
 * @returns {object} - The configuration object for the "General" tab
 */
function buildGeneralTab({ urlError = false }) {
  return {
    name: "general",
    title: i18n.t("htmlEditor.imageMenu.tab.general"),
    items: [
      {
        type: "htmlpanel",
        html: `<span style="${buildUrlInputLabelStyles({
          urlError,
        })}"><label>${i18n.t(
          "htmlEditor.imageMenu.input.source"
        )}</label><span class="ph ph-info" style="margin-left: 4px" title="${i18n.t(
          "htmlEditor.imageMenu.sourceTooltip"
        )}"></span></span>`,
      },
      {
        type: "input",
        inputmode: "url",
        name: "src",
      },
      {
        type: "htmlpanel",
        html: `<div style="${buildErrorMessageStyles({ urlError })}">${i18n.t(
          "errorMessages.photoLink"
        )}</div>`,
      },
      {
        type: "input",
        name: "alt",
        label: i18n.t("htmlEditor.imageMenu.input.alt"),
      },
      {
        type: "bar",
        align: "end",
        items: [
          {
            type: "sizeinput",
            name: "size",
            label: i18n.t("htmlEditor.imageMenu.input.sizes"),
          },
        ],
      },
      {
        type: "selectbox",
        name: "borderClass",
        label: i18n.t("htmlEditor.imageMenu.input.borders"),
        items: [
          { value: "", text: i18n.t("htmlEditor.imageMenu.border.none") },
          {
            value: "img_rounded_border",
            text: i18n.t("htmlEditor.imageMenu.border.rounded"),
          },
          {
            value: "img_circle_border",
            text: i18n.t("htmlEditor.imageMenu.border.circle"),
          },
          {
            value: "img_square_border",
            text: i18n.t("htmlEditor.imageMenu.border.square"),
          },
        ],
      },
    ].filter((item) => item),
  };
}

/**
 * Builds the "Advanced" tab configuration for the image menu.
 * Includes fields for custom styles, margins, border width, and border style.
 * @returns {object} - The configuration object for the "Advanced" tab.
 */
function buildAdvancedTab() {
  return {
    name: "advanced",
    title: i18n.t("htmlEditor.imageMenu.tab.advanced"),
    items: [
      {
        type: "input",
        name: "style",
        label: i18n.t("htmlEditor.imageMenu.input.style"),
        placeholder: i18n.t("htmlEditor.imageMenu.stylePlaceholder"),
        disabled: true,
      },
      {
        type: "grid",
        columns: 2,
        items: [
          {
            type: "input",
            name: "verticalMargin",
            label: i18n.t("htmlEditor.imageMenu.input.verticalMargin"),
            inputmode: "numeric",
          },
          {
            type: "input",
            name: "horizontalMargin",
            label: i18n.t("htmlEditor.imageMenu.input.horizontalMargin"),
            inputmode: "numeric",
          },
        ],
      },
      {
        type: "grid",
        columns: 2,
        items: [
          {
            type: "input",
            name: "borderWidth",
            label: i18n.t("htmlEditor.imageMenu.input.borderWidth"),
            inputmode: "numeric",
          },
          {
            type: "selectbox",
            name: "borderStyle",
            label: i18n.t("htmlEditor.imageMenu.input.borderStyle"),
            items: [
              {
                value: "",
                text: i18n.t("htmlEditor.imageMenu.borderStyle.select"),
              },
              {
                value: "solid",
                text: i18n.t("htmlEditor.imageMenu.borderStyle.solid"),
              },
              {
                value: "dotted",
                text: i18n.t("htmlEditor.imageMenu.borderStyle.dotted"),
              },
              {
                value: "dashed",
                text: i18n.t("htmlEditor.imageMenu.borderStyle.dashed"),
              },
              {
                value: "double",
                text: i18n.t("htmlEditor.imageMenu.borderStyle.double"),
              },
              {
                value: "groove",
                text: i18n.t("htmlEditor.imageMenu.borderStyle.groove"),
              },
              {
                value: "ridge",
                text: i18n.t("htmlEditor.imageMenu.borderStyle.ridge"),
              },
              {
                value: "inset",
                text: i18n.t("htmlEditor.imageMenu.borderStyle.inset"),
              },
              {
                value: "outset",
                text: i18n.t("htmlEditor.imageMenu.borderStyle.outset"),
              },
              {
                value: "none",
                text: i18n.t("htmlEditor.imageMenu.borderStyle.none"),
              },
              {
                value: "hidden",
                text: i18n.t("htmlEditor.imageMenu.borderStyle.hidden"),
              },
            ],
          },
        ],
      },
    ],
  };
}

/**
 * Builds the main body configuration for the image menu, which is a tab panel
 * containing the "General" and "Advanced" tabs.
 * @param {object} { editor, urlError = false } - An object containing the editor instance and a boolean indicating a URL error.
 * @returns {object} - The configuration object for the menu body
 */
function buildBody({ editor, urlError = false }) {
  return {
    type: "tabpanel",
    tabs: [buildGeneralTab({ editor, urlError }), buildAdvancedTab()],
  };
}

/**
 * Builds the button configuration for the image menu, including "Cancel" and "Save" buttons.
 * @returns {Array<object>} - An array of button configuration objects.
 * Each object has `type`, `text`, and optionally `primary` properties
 */
function buildButtons({ urlError = false }) {
  return [
    {
      type: "cancel",
      text: i18n.t("action.cancel"),
    },
    {
      type: "submit",
      text: i18n.t("action.save"),
      primary: true,
      disabled: urlError,
    },
  ];
}

/**
 * Builds a CSS style string from the provided image data, including border and margin styles.
 * Handles cases where some style properties might be missing.
 * @param {object} data - The image data object containing style-related properties.
 * @returns {string} - A CSS style string that can be applied to an image element.
 */
function buildStylesString(data) {
  const borderWidth = data.borderWidth
    ? `border-width: ${data.borderWidth}px;`
    : "";

  const borderStyle = data.borderStyle
    ? `border-style: ${data.borderStyle};`
    : "";

  const verticalMargin = data.verticalMargin || "0";
  const horizontalMargin = data.horizontalMargin || "0";
  const margin =
    horizontalMargin || verticalMargin
      ? `margin: ${verticalMargin}px ${horizontalMargin}px ${verticalMargin}px ${horizontalMargin}px;`
      : "";

  return `${borderWidth} ${borderStyle} ${margin}`.trim();
}

/**
 * Creates a debounced function to handle changes in the image menu.
 * Updates the menu state based on changes to the image source (URL) or advanced style properties.
 * @param {object} editor - The editor instance.
 * @returns {Function} - A debounced function that takes the API object and details of the change as arguments.
 * It updates the menu's data and potentially redials the menu with updated configurations
 */
function buildOnChangeDebounced(editor) {
  return debounce(async function onAction(api, details) {
    const data = api.getData();

    // handle src changes
    if (details.name === "src") {
      try {
        const isPublic = await isPublicLink(data.src);
        urlError = !isPublic;
      } catch (e) {
        urlError = true;
      }
      api.redial(buildMenu({ editor, urlError, ...data }));
    }

    // handle advanced style changes
    if (
      [
        "borderClass",
        "verticalMargin",
        "horizontalMargin",
        "borderWidth",
        "borderStyle",
      ].includes(details.name)
    ) {
      const styles = buildStylesString(data);
      api.setData({ style: styles });
    }
  }, 400);
}

/**
 * Asynchronously builds an HTML string for an image element based on the provided data.
 * Calculates the image dimensions, considering aspect ratio and default values.
 * @param {object} data - The image data object containing properties like src, alt, size, borderClass, and styles.
 * @returns {Promise<string>} - A promise that resolves to an HTML string representing the image element,
 * including attributes for class, style, src, alt, width, and height
 */
async function buildImageElementString(data) {
  const borderClass = data.borderClass;
  const styles = buildStylesString(data);
  const imageSize = await getImageSizeFromUrl(data.src);
  const width = parseInt(data.size.width) || 200;
  const height = data.size.height
    ? parseInt(data.size.height)
    : imageSize.width
    ? (width / imageSize.width) * imageSize.height
    : 200;
  return `<img class="${borderClass}" style="${styles}" src="${data.src}" alt="${data.alt}" width="${width}" height="${height}" />`;
}

/**
 * Builds the onSubmit function for the image menu.
 * This function is called when the user submits the menu form.
 * It validates the image URL, builds the image element string, and inserts it into the editor.
 * @param {object} editor - The editor instance.
 * @returns {Function} - An asynchronous function that takes the API object as an argument.
 */
function buildOnSubmit(editor) {
  return async function onSubmit(api) {
    const data = api.getData() || {};
    const isPublic = await isPublicLink(data.src);
    urlError = !isPublic;
    if (urlError) {
      api.redial(buildMenu({ editor, urlError, ...data }));
    } else {
      const imageElementString = await buildImageElementString(data);
      editor.insertContent(imageElementString);
      api.close();
    }
  };
}

/**
 * Builds the configuration object for the image menu dialog.
 * This object includes the menu's title, initial data, body (tabs), buttons, onSubmit handler, and onChange handler.
 * @param {object} { editor, urlError, ...data } - An object containing the editor instance, a URL error flag, and any initial data.
 * @returns {object} - The configuration object for the image menu dialog.
 */
function buildMenu({ editor, urlError, ...data }) {
  return {
    title: menuDialogLabel,
    initialData: data,
    body: buildBody({ editor, urlError, ...data }),
    buttons: buildButtons({ urlError }),
    onSubmit: buildOnSubmit(editor),
    onChange: buildOnChangeDebounced(editor),
  };
}

/**
 * Builds the image editor menu item that will be added to the editor's toolbar or menu.
 * This item allows users to open the image properties dialog.
 * Documentation on how to build dialog menus: https://www.tiny.cloud/docs/tinymce/5/dialog-components/#layoutcomponents
 * @param {object} { editor } - An object containing the editor instance.
 * @returns {object} - An object representing the menu item, with properties for text, icon, and onAction (the function to execute when the item is clicked)
 */
export function buildImageEditorMenu({ editor }) {
  return {
    text: menuOptionLabel,
    icon: "image",
    onAction() {
      urlError = false;
      const node = editor.selection?.getNode();
      const isImage = node && node.nodeName.toLowerCase() === "img";
      let data;
      if (isImage && node) {
        data = buildInitialDataFromNode(node);
        if (node.src) {
          isPublicLink(node.src).then((isPublic) => {
            urlError = !isPublic;
          });
        }
      } else {
        data = buildInitialDataDefault();
      }
      return editor.windowManager.open(
        buildMenu({ editor, urlError, ...data })
      );
    },
  };
}
